import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Icon,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Tooltip
} from '@chakra-ui/react';
import { FaUsers, FaWallet, FaClock, FaChartLine, FaUser, FaChevronRight } from 'react-icons/fa';
import { useWalletContext } from '../../context/WalletContext';

const LinkedArtistsSummary = ({ onClick }) => {
  const { walletData, formatCurrency } = useWalletContext();

  const linkedArtistsSummary = walletData?.linked_artists_summary;

  if (!linkedArtistsSummary || linkedArtistsSummary.total_artists === 0) {
    return null;
  }

  const StatCard = ({ icon, label, value, helpText, color = "primary" }) => (
    <Box bg="dark.500" borderRadius="lg" p={4} border="1px solid" borderColor="dark.300">
      <Stat>
        <StatLabel color="gray.400" fontSize="sm">
          <HStack spacing={2}>
            <Icon as={icon} color={`${color}.400`} boxSize={4} />
            <Text>{label}</Text>
          </HStack>
        </StatLabel>
        <StatNumber color="white" fontSize="xl">
          {value}
        </StatNumber>
        {helpText && (
          <StatHelpText color="gray.500" fontSize="xs">
            {helpText}
          </StatHelpText>
        )}
      </Stat>
    </Box>
  );

  return (
    <Box bg="secondary.100" borderRadius="xl" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Clickable Header and Stats Section */}
        <Tooltip
          label={onClick ? "Click to manage artists" : ""}
          hasArrow
          placement="top"
          isDisabled={!onClick}
        >
          <Box
            onClick={onClick}
            cursor={onClick ? "pointer" : "default"}
            transition="all 0.2s"
            _hover={onClick ? { bg: 'whiteAlpha.50' } : {}}
            borderRadius="lg"
            p={4}
            mx={-4}
          >
            {/* Header */}
            <HStack justify="space-between" align="center" mb={6}>
              <HStack spacing={3}>
                <Icon as={FaUsers} color="primary.400" boxSize={6} />
                <Text fontSize="xl" fontWeight="bold" color="white">
                  Linked Artists Overview
                </Text>
                {onClick && (
                  <Icon as={FaChevronRight} color="primary.400" boxSize={4} />
                )}
              </HStack>
              <Badge colorScheme="blue" variant="solid" fontSize="sm" px={3} py={1}>
                {linkedArtistsSummary.total_artists} Artist{linkedArtistsSummary.total_artists !== 1 ? 's' : ''}
              </Badge>
            </HStack>

          {/* Aggregate Stats */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <StatCard
              icon={FaWallet}
              label="Total Balance"
              value={formatCurrency(linkedArtistsSummary.total_available_balance)}
              helpText="All artists combined"
              color="green"
            />
            <StatCard
              icon={FaClock}
              label="Total Pending"
              value={formatCurrency(linkedArtistsSummary.total_pending_earnings)}
              helpText="Awaiting clearance"
              color="yellow"
            />
            <StatCard
              icon={FaChartLine}
              label="Total Earned"
              value={formatCurrency(linkedArtistsSummary.total_earned)}
              helpText="All-time earnings"
              color="blue"
            />
            <StatCard
              icon={FaWallet}
              label="Total Spent"
              value={formatCurrency(linkedArtistsSummary.total_spent)}
              helpText="All purchases"
              color="purple"
            />
          </SimpleGrid>
          </Box>
        </Tooltip>

        <Divider borderColor="dark.300" />

        {/* Individual Artists Breakdown */}
        <Box>
          <Text fontSize="md" fontWeight="semibold" color="white" mb={3}>
            Artist Breakdown
          </Text>
          <Accordion allowToggle>
            {linkedArtistsSummary.artists.map((artist) => (
              <AccordionItem key={artist.artist_id} border="1px solid" borderColor="dark.300" borderRadius="md" mb={2}>
                <AccordionButton _hover={{ bg: 'dark.500' }} borderRadius="md">
                  <HStack flex="1" spacing={3} textAlign="left">
                    <Icon as={FaUser} color="primary.400" boxSize={4} />
                    <VStack align="start" spacing={0} flex={1}>
                      <Text fontSize="sm" fontWeight="medium" color="white">
                        {artist.artist_name}
                      </Text>
                      <Text fontSize="xs" color="gray.400">
                        {artist.email}
                      </Text>
                    </VStack>
                    <Text fontSize="sm" fontWeight="bold" color="green.400">
                      {formatCurrency(artist.available_balance)}
                    </Text>
                  </HStack>
                  <AccordionIcon color="gray.400" />
                </AccordionButton>
                <AccordionPanel pb={4} bg="dark.700" borderRadius="md">
                  <SimpleGrid columns={2} spacing={4}>
                    <Box>
                      <Text fontSize="xs" color="gray.400">Available Balance</Text>
                      <Text fontSize="md" fontWeight="bold" color="green.400">
                        {formatCurrency(artist.available_balance)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.400">Pending Earnings</Text>
                      <Text fontSize="md" fontWeight="bold" color="yellow.400">
                        {formatCurrency(artist.pending_earnings)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.400">Total Earned</Text>
                      <Text fontSize="md" fontWeight="medium" color="blue.400">
                        {formatCurrency(artist.total_earned)}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.400">Total Spent</Text>
                      <Text fontSize="md" fontWeight="medium" color="purple.400">
                        {formatCurrency(artist.total_spent)}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>
      </VStack>
    </Box>
  );
};

export default LinkedArtistsSummary;
